import { JSX, ReactNode } from 'react';
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export interface AdminMiniDialogProps {
    open: boolean;
    onClose: () => void;
    title: string;
    /** A short line under the title saying what this window is for. Optional. */
    subtitle?: string;
    /** Buttons for the title bar, which is where a window's own actions belong. */
    actions?: ReactNode;
    /** `lg` for the composer, `md` for a single conversation. */
    width?: 'md' | 'lg' | 'xl';
    children: ReactNode;
}

/**
 * The window every admin Mini screen opens in.
 *
 * **The three modals it replaces were all `fullScreen`** (user, 2026-09-11), which is the wrong
 * shape for this work in a way that costs more than looks. An admin building a run is comparing it
 * against the list of runs behind it, and a window that covers the entire viewport hides the thing
 * they are working from and gives no way back except closing. It also stretched a two-column form
 * across a 27-inch display, so the editor and the recipient list ended up a screen's width apart.
 *
 * Sized instead: wide enough for two columns and a rich text editor, short enough to keep the page
 * visible around it, and capped rather than proportional so it does not keep growing with the
 * display. **It still goes full screen on a small viewport**, where a sized dialog is worse than
 * none - MUI's own breakpoint decides that rather than a guess about what an admin is using.
 *
 * The close control is a real `IconButton` with a label. It was a bare `<button>` carrying only an
 * icon, which reads to a screen reader as an unnamed button and gets no focus ring.
 */
export const AdminMiniDialog: (props: AdminMiniDialogProps) => JSX.Element =
    (props: AdminMiniDialogProps): JSX.Element => {
        const theme = useTheme();
        // Below `md` a dialog with margins is a dialog with no room in it.
        const smallViewport: boolean = useMediaQuery(theme.breakpoints.down('md'));

        return (
            <Dialog
                open={ props.open }
                onClose={ props.onClose }
                fullScreen={ smallViewport }
                fullWidth
                maxWidth={ props.width ?? 'lg' }
                slotProps={ {
                    paper: {
                        sx: {
                            // Capped, not proportional. A dialog that tracks the viewport is a
                            // full-screen dialog again on a large display.
                            maxWidth: props.width === 'md' ? '860px' : '1180px',
                            height: smallViewport ? '100%' : 'min(88vh, 940px)',
                            borderRadius: smallViewport ? 0 : '14px',
                        },
                    },
                } }
            >
                <DialogTitle
                    component="div"
                    sx={ {
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: '16px',
                        borderBottom: '1px solid',
                        borderBottomColor: 'divider',
                        paddingBottom: '14px',
                    } }
                >
                    <Box sx={ { minWidth: 0 } }>
                        <Typography component="h2" sx={ { fontSize: '18px', fontWeight: 700, lineHeight: 1.3 } }>
                            { props.title }
                        </Typography>
                        { props.subtitle !== undefined && (
                            <Typography sx={ {
                                fontSize: 'var(--wf-eyebrow)',
                                letterSpacing: 'var(--wf-track)',
                                textTransform: 'uppercase',
                                color: 'text.secondary',
                                fontWeight: 700,
                                marginTop: '4px',
                            } }>
                                { props.subtitle }
                            </Typography>
                        ) }
                    </Box>

                    <Box sx={ { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 } }>
                        { props.actions }
                        <IconButton onClick={ props.onClose } aria-label="Close" size="small">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>

                { /* The content scrolls, not the window. A title bar that scrolls away takes the
                     close control with it, which on a full-screen dialog left no way out at all. */ }
                <DialogContent sx={ { paddingTop: '18px', overflowY: 'auto' } }>
                    { props.children }
                </DialogContent>
            </Dialog>
        );
    };
